import { Destination, CostRange } from "../context/SearchTypes";

// Updated default cost ranges with correct caps
export const DEFAULT_COST_RANGES: Record<string, [number, number]> = {
  'avg_daily_budget': [0, 300],
  'avg_accommodation_cost': [0, 200],
  'avg_meal_cost': [0, 100],
  'taxi_cost_per_km': [0, 20]
};

// Get the correct maximum value for a cost type
export const getMaxValueForCostType = (costType: string): number => {
  switch (costType) {
    case 'avg_accommodation_cost': return 200;
    case 'avg_meal_cost': return 100;
    case 'avg_daily_budget': return 300;
    case 'taxi_cost_per_km': return 20;
    default: return 200;
  }
};

export const calculateCostRanges = (destinations: Destination[]): CostRange => {
  // Start with a copy of the default ranges
  const ranges: Record<string, [number, number]> = { ...DEFAULT_COST_RANGES };
  
  // Cost types to analyze
  const costTypes = ['avg_daily_budget', 'avg_accommodation_cost', 'avg_meal_cost', 'taxi_cost_per_km'];
  
  // Initialize with default extreme values
  costTypes.forEach(type => {
    ranges[type] = [Infinity, -Infinity];
  });
  
  // Calculate actual min/max for each cost type
  if (Array.isArray(destinations) && destinations.length > 0) {
    console.log(`Calculating cost ranges for ${destinations.length} destinations`);
    
    destinations.forEach(dest => {
      if (!dest || !dest.costs_infrastructure) return;
      
      costTypes.forEach(type => {
        const costRaw = dest.costs_infrastructure[type];
        if (costRaw === undefined || costRaw === null) return;
        
        const cost = typeof costRaw === 'number' ? costRaw : parseFloat(String(costRaw));
        if (isNaN(cost)) return;
        
        // Update min if this value is smaller
        if (cost < ranges[type][0]) {
          ranges[type][0] = cost;
        }
        
        // Update max if this value is larger
        if (cost > ranges[type][1]) {
          ranges[type][1] = cost;
        }
      });
    });
  } else {
    console.log('No destinations available for cost range calculation, using defaults');
  }
  
  // Replace any Infinity values with defaults and apply caps
  costTypes.forEach(type => {
    if (ranges[type][0] === Infinity) ranges[type][0] = DEFAULT_COST_RANGES[type][0];
    if (ranges[type][1] === -Infinity) ranges[type][1] = DEFAULT_COST_RANGES[type][1];
    
    // Round the values for better UX
    ranges[type][0] = Math.floor(ranges[type][0]);
    
    // Add a small buffer to the max (10% more) for better UX
    let maxValue = Math.ceil(ranges[type][1] * 1.1);
    
    // Apply hard caps based on cost type
    const cappedValue = getMaxValueForCostType(type);
    if (maxValue > cappedValue) {
      maxValue = cappedValue;
    }
    
    ranges[type][1] = maxValue;
  });
  
  console.log('Calculated cost ranges with caps:', ranges);
  return ranges;
};

// Check if a destination is within the price range for the selected cost type
export const isDestinationInPriceRange = (
  destination: Destination,
  costType: string,
  priceRange: [number, number]
): boolean => {
  if (!destination?.costs_infrastructure || !costType) {
    console.log(`Skipping destination ${destination?.name || 'unknown'} - no costs_infrastructure or costType`);
    return false;
  }

  const costValue = destination.costs_infrastructure[costType];
  if (costValue === undefined || costValue === null) {
    console.log(`Skipping destination ${destination.name} - no value for ${costType}`);
    return false;
  }

  const cost = typeof costValue === 'number' ? costValue : parseFloat(String(costValue));
  if (isNaN(cost)) {
    console.log(`Skipping destination ${destination.name} - NaN value for ${costType}: ${costValue}`);
    return false;
  }

  const [min, max] = priceRange;
  const result = cost >= min && cost <= max;
  
  console.log(`Price check for ${destination.name}: ${costType}=${cost}, range=[${min},${max}], result=${result}`);
  return result;
};

// Get all unique cost values for a particular cost type
export const getUniqueCostValues = (destinations: Destination[], costType: string): number[] => {
  if (!destinations || !Array.isArray(destinations)) return [];
  
  const values = new Set<number>();
  
  destinations.forEach(dest => {
    if (!dest.costs_infrastructure) return;
    
    const costRaw = dest.costs_infrastructure[costType];
    if (costRaw === undefined || costRaw === null) return;
    
    const cost = typeof costRaw === 'number' ? costRaw : parseFloat(String(costRaw));
    if (isNaN(cost)) return;
    
    values.add(cost);
  });
  
  return Array.from(values).sort((a, b) => a - b);
};

// Analyze distribution of cost values to generate histogram data
export const generateCostHistogram = (destinations: Destination[], costType: string, bins: number = 10): number[] => {
  const values = getUniqueCostValues(destinations, costType);
  
  if (values.length === 0) {
    return Array(bins).fill(1); // Return flat distribution if no data
  }
  
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;
  
  if (range === 0) {
    return Array(bins).fill(values.length); // All values are the same
  }
  
  // Initialize bins array
  const histogramBins = Array(bins).fill(0);
  
  // Count values in each bin
  values.forEach(value => {
    const binIndex = Math.min(Math.floor((value - min) / range * bins), bins - 1);
    histogramBins[binIndex]++;
  });
  
  // Smooth histogram for better visual appeal
  const smoothedBins = smoothHistogram(histogramBins);
  
  return smoothedBins;
};

// Apply Gaussian-like smoothing to histogram data
const smoothHistogram = (bins: number[]): number[] => {
  if (!Array.isArray(bins) || bins.length <= 3) return bins;
  
  const smoothed = [...bins];
  const iterations = 1;
  
  for (let iteration = 0; iteration < iterations; iteration++) {
    const temp = [...smoothed];
    
    for (let i = 0; i < bins.length; i++) {
      let sum = 0;
      let weights = 0;
      
      for (let j = -2; j <= 2; j++) {
        const index = i + j;
        if (index >= 0 && index < bins.length) {
          const weight = Math.exp(-(j * j) / 4);
          sum += temp[index] * weight;
          weights += weight;
        }
      }
      
      smoothed[i] = sum / weights;
    }
  }
  
  return smoothed;
};
